// Home.jsx
import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";

const roles = [
  { text: "Investors", color: "text-blue-400", bgColor: "from-blue-500/20 to-cyan-500/10", shadowColor: "shadow-blue-500/50" },
  { text: "Crypto & Forex Traders", color: "text-purple-400", bgColor: "from-purple-500/20 to-pink-500/10", shadowColor: "shadow-purple-500/50" },
  { text: "Tech-Savvy Developers", color: "text-green-400", bgColor: "from-green-500/20 to-emerald-500/10", shadowColor: "shadow-green-500/50" },
  { text: "Quant Curious Students", color: "text-yellow-400", bgColor: "from-yellow-500/20 to-orange-500/10", shadowColor: "shadow-yellow-500/50" },
  { text: "Institutions & Hedge Funds", color: "text-red-400", bgColor: "from-red-500/20 to-rose-500/10", shadowColor: "shadow-red-500/50" },
];

// Digital Hologram Text Effect - Unique creative animation
const DigitalHologramText = () => {

  const [currentIndex, setCurrentIndex] = useState(0);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % roles.length);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  const currentRole = roles[currentIndex];

  // Create particle effect elements
  const particles = Array.from({ length: 8 }, (_, i) => (
    <motion.div
      key={i}
      className="absolute w-1 h-1 bg-blue-400 rounded-full"
      initial={{ opacity: 0, scale: 0 }}
      animate={{
        opacity: [0, 1, 0],
        scale: [0, 1, 0],
        x: [0, Math.random() * 100 - 50],
        y: [0, Math.random() * 100 - 50],
      }}
      transition={{
        duration: 2,
        repeat: Infinity,
        delay: i * 0.2,
        ease: "easeOut"
      }}
    />
  ));

  return (
    <div className="relative inline-block">
      {/* Background glow effect */}
      <motion.div
        className={`absolute -inset-2 bg-gradient-to-r ${currentRole.bgColor} rounded-lg blur-sm`}
        animate={{ opacity: [0.3, 0.6, 0.3] }}
        transition={{ duration: 2, repeat: Infinity }}
      />

      {/* Particles */}
      <div className="absolute inset-0 overflow-hidden">{particles}</div>

      {/* Hologram scan lines */}
      <motion.div
        className="absolute inset-0 bg-gradient-to-b from-transparent via-blue-400/10 to-transparent"
        animate={{ y: ["-100%", "100%"] }}
        transition={{ duration: 1.5, repeat: Infinity, ease: "linear" }}
      />

      {/* Main text with morphing effect */}
      <AnimatePresence mode="wait">
        <motion.div
          key={currentIndex}
          initial={{
            opacity: 0,
            scale: 0.8,
            rotateX: -90,
            filter: "blur(10px) brightness(0.5)"
          }}
          animate={{
            opacity: 1,
            scale: 1,
            rotateX: 0,
            filter: "blur(0px) brightness(1)"
          }}
          exit={{
            opacity: 0,
            scale: 1.2,
            rotateX: 90,
            filter: "blur(10px) brightness(2)"
          }}
          transition={{ duration: 0.8, ease: "easeInOut" }}
          className={`relative px-4 py-2 ${currentRole.color} font-bold text-2xl sm:text-3xl md:text-4xl`}
          style={{
            textShadow: "0 0 20px currentColor, 0 0 40px currentColor",
            fontFamily: "monospace",
            letterSpacing: "0.1em"
          }}
        >
          {/* Glitch overlay effect */}
          <motion.div
            className="absolute inset-0 text-red-400 opacity-50"
            animate={{
              x: [0, 2, -2, 0],
              opacity: [0, 0.3, 0]
            }}
            transition={{
              duration: 0.2,
              repeat: Infinity,
              repeatDelay: 4,
              ease: "easeInOut"
            }}
          >
            {currentRole.text}
          </motion.div>

          {/* Main text */}
          <motion.span
            animate={{
              filter: ["hue-rotate(0deg)", "hue-rotate(360deg)"]
            }}
            transition={{ duration: 6, repeat: Infinity, ease: "linear" }}
          >
            {currentRole.text}
          </motion.span>

          {/* Matrix rain effect */}
          {Array.from({ length: 6 }, (_, i) => (
            <motion.span
              key={i}
              className="absolute text-green-400 opacity-30 text-xs"
              style={{
                left: `${20 + i * 15}%`,
                top: "100%",
                fontFamily: "monospace"
              }}
              animate={{
                y: [0, 50],
                opacity: [0.5, 0]
              }}
              transition={{
                duration: 1,
                repeat: Infinity,
                delay: i * 0.3,
                ease: "linear"
              }}
            >
              {Math.random().toString(36).substring(7)}
            </motion.span>
          ))}
        </motion.div>
      </AnimatePresence>
    </div>
  );
};

// Staggered text animation component
const StaggeredText = ({ children, className = "" }) => {
  const words = children.split(" ");

  return (
    <div className={className}>
      {words.map((word, i) => (
        <motion.span
          key={i}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: i * 0.1, duration: 0.8 }}
          className="inline-block mr-2"
        >
          {word}
        </motion.span>
      ))}
    </div>
  );
};

const Home = () => {
  return (
    <div className="flex justify-center items-center min-h-screen px-4 py-8 md:py-0">
      <div className="text-left w-full max-w-2xl mx-auto">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1, ease: "easeOut" }}
        >
          <StaggeredText className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-white font-orbitron leading-snug mb-4 sm:mb-6">
            Innovative Trading Tech
          </StaggeredText>

          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.8, duration: 0.8 }}
            className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-white font-orbitron leading-snug mb-6 sm:mb-8"
          >
            Built for the New Generation of{" "}
            <br className="block" />
            <div className="mt-4">
              <DigitalHologramText />
            </div>
          </motion.div>
        </motion.div>

        <motion.p
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.5, duration: 0.8 }}
          className="text-base sm:text-lg md:text-xl text-gray-300 font-orbitron leading-relaxed mb-6 sm:mb-8 md:mb-10"
        >
          KSQuant delivers{" "}
          <motion.span
            className="bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent font-semibold"
            animate={{ backgroundPosition: ["0%", "100%", "0%"] }}
            transition={{ duration: 3, repeat: Infinity }}
          >
            algorithmic trading tools
          </motion.span>
          , quant finance education, and smart fintech software, all optimized for{" "}
          <motion.span
            className="bg-gradient-to-r from-green-400 to-cyan-400 bg-clip-text text-transparent font-semibold"
            animate={{ backgroundPosition: ["0%", "100%", "0%"] }}
            transition={{ duration: 3, repeat: Infinity, delay: 1 }}
          >
            growth-minded traders
          </motion.span>{" "}
          and data-driven thinkers.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 2, duration: 0.8 }}
          className="flex flex-col sm:flex-row gap-4 sm:gap-6 mt-6 sm:mt-8"
        >
          {/* Primary CTA Button */}
          <motion.div
            whileHover={{
              scale: 1.05,
              y: -3,
              boxShadow: "0 20px 40px rgba(59, 130, 246, 0.5)"
            }}
            whileTap={{ scale: 0.98 }}
            transition={{ type: "spring", stiffness: 300, damping: 20 }}
          >
            <a
              href="https://protrader.ksquants.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="group relative inline-flex items-center justify-center px-6 sm:px-8 py-3 sm:py-4 text-base sm:text-lg font-bold text-white bg-gradient-to-r from-blue-600 via-blue-700 to-purple-700 rounded-xl shadow-2xl hover:shadow-blue-500/50 transition-all duration-300 overflow-hidden border border-blue-500/30"
            >
              {/* Animated background */}
              <motion.div
                className="absolute inset-0 bg-gradient-to-r from-purple-600 via-blue-600 to-cyan-600"
                initial={{ x: "-100%" }}
                whileHover={{ x: "0%" }}
                transition={{ duration: 0.3 }}
              />

              {/* Glow effect */}
              <div className="absolute inset-0 bg-gradient-to-r from-blue-400/20 to-purple-400/20 blur-xl" />

              {/* Button text */}
              <span className="relative z-10 flex items-center gap-3 font-orbitron">
                <motion.span
                  animate={{
                    scale: [1, 1.15, 1],
                    y: [0, -2, 0]
                  }}
                  transition={{
                    duration: 2,
                    repeat: Infinity,
                    ease: "easeInOut"
                  }}
                >
                </motion.span>
                Honcraft
              </span>

              {/* Shine effect */}
              <motion.div
                className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -skew-x-12"
                initial={{ x: "-100%" }}
                animate={{ x: "200%" }}
                transition={{
                  duration: 2,
                  repeat: Infinity,
                  repeatDelay: 3,
                  ease: "easeInOut"
                }}
              />
            </a>
          </motion.div>

          {/* Secondary CTA Button */}
          <motion.div
            whileHover={{
              scale: 1.05,
              y: -3,
              boxShadow: "0 20px 40px rgba(147, 51, 234, 0.3)"
            }}
            whileTap={{ scale: 0.98 }}
            transition={{ type: "spring", stiffness: 300, damping: 20 }}
          >
            <Link
              to="/algo-trading-academy"
              className="group relative inline-flex items-center justify-center px-6 sm:px-8 py-3 sm:py-4 text-base sm:text-lg font-bold text-purple-400 bg-transparent rounded-xl border-2 border-purple-400/50 hover:border-purple-400/70 transition-all duration-300 overflow-hidden backdrop-blur-sm"
            >
              {/* Animated background on hover */}
              <motion.div
                className="absolute inset-0 bg-gradient-to-r from-purple-500/10 via-pink-500/10 to-purple-500/10"
                initial={{ opacity: 0, scale: 0.8 }}
                whileHover={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.3 }}
              />

              {/* Button text */}
              <span className="relative z-10 flex items-center gap-3 font-orbitron">
                <motion.span
                  animate={{
                    scale: [1, 1.3, 1],
                    opacity: [0.7, 1, 0.7]
                  }}
                  transition={{
                    duration: 2.5,
                    repeat: Infinity,
                    ease: "easeInOut"
                  }}
                >
                  🎓
                </motion.span>
                Join Quant Academy
              </span>

              {/* Border glow effect */}
              <motion.div
                className="absolute inset-0 rounded-xl border-2 border-purple-400/30"
                animate={{
                  borderColor: ["rgba(147, 51, 234, 0.3)", "rgba(219, 39, 119, 0.5)", "rgba(147, 51, 234, 0.3)"]
                }}
                transition={{
                  duration: 3,
                  repeat: Infinity,
                  ease: "easeInOut"
                }}
              />
            </Link>
          </motion.div>
        </motion.div>
      </div>
    </div>
  );
};

export default Home;
